import {useState, useCallback, useEffect} from 'react';
import {TabType} from '../types';
import {useSelector} from 'react-redux';
import {TState} from '../../../redux/store.type';
import {getEventSettings} from '../../../redux/event/eventActions';
import setGlobalState from '../../../redux/setGlobalState';
import {validateWhatsAppText} from '../../../pages/branding/whatsAppScreen/whatsAppValidation';
import {useTranslation} from 'react-i18next';
import {serializeTemplateStructure} from '../../whatsAppTemplateEditor/utils/formatUtils';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';
import useRestartXHR from '../../../hooks/useRestartXHR';

interface UseSendingLogicProps {
    whatsappProps: any;
    smsProps: any;
    emailProps: any;
    amount: number;
    onHasEnoughCredits?: () => void;
    allowedList: string[];
    initialTab?: TabType;
}

export const useSendingLogic = ({whatsappProps, smsProps, emailProps, amount, onHasEnoughCredits, allowedList, initialTab}: UseSendingLogicProps) => {
    const {t} = useTranslation('branding');
    const {t: tMailing} = useTranslation('mailing');
    const whatsappPermission = useSelector((s: TState) => s.account.permissions?.find(p => p.permission === 'whatsApp')?.status === 1);
    const getEventSettingsXHR = useSelector((state: TState) => state.event.getEventSettingsXHR);
    const account = useSelector((state: TState) => state.account);
    const [currentTab, setCurrentTab] = useState<TabType | null>(null);
    const [sendingTab, setSendingTab] = useState<TabType | null>(null);
    const [trialContacts, setTrialContacts] = useState<string[] | null>(null);
    const [isTrialSendFlow, setIsTrialSendFlow] = useState(false);
    let whatsappAllowed = whatsappPermission;
    if(!allowedList.includes('whatsapp') && whatsappAllowed) {
        whatsappAllowed = false;
    }
    const filteredAllowedList: string[] = whatsappAllowed
        ? allowedList
        : allowedList.filter((item: string) => item !== 'whatsapp');

    const props = {
        whatsapp: whatsappProps,
        sms: smsProps,
        email: emailProps
    };
    const sendingXHR = props[currentTab]?.sendingXHR;
    const saveMailingXHR = useSelector((state: TState) => state.mailing.saveMailingXHR);
    const saveFreeWhatsAppMessageXHR = useSelector((state: TState) => state.branding.saveFreeWhatsAppMessageXHR);
    const saveXHR = saveFreeWhatsAppMessageXHR ?? saveMailingXHR;
    useRestartXHR(saveXHR, 'mailing.saveMailingXHR');
    useRestartXHR(saveFreeWhatsAppMessageXHR, 'branding.saveFreeWhatsAppMessageXHR');
    const validateWhatsAppContent = () => {
        if(currentTab === 'whatsapp') {
            const rawMessage = whatsappProps?.message;
            const hasFreeMessage = rawMessage != null && String(rawMessage).trim() !== '';
            const templateText = serializeTemplateStructure(whatsappProps?.whatsappTemplate ?? []);
            const textForValidation = hasFreeMessage ? rawMessage : templateText;
            return validateWhatsAppText(textForValidation, t);
        }
        return true;
    };

    const handleTabChange = (tab: TabType) => {
        setCurrentTab(tab);
    };
    const checkIfHasEnoughCredits = () => {
        if(currentTab === 'whatsapp') {
            return true;
        }
        if(currentTab === 'sms') {
            const smsCredits = account.billPackage?.smsFreeAmount || 0;
            return smsCredits >= amount;
        } else if(currentTab === 'email') {
            const emailCredits = account.billPackage?.emailFreeAmount || 0;
            return emailCredits >= amount;
        }
        return true;
    };

    const handleSave = async () => {
        try {
            const result = await props[currentTab]?.save?.();
            if(result !== false) {
                bounceMessage(tMailing('saveSuccess'), 'success');
            }
        } catch {
            bounceMessage(tMailing('wentWrong'), 'error');
        }
    };

    const handleSend = async () => {
        try {
            if(!validateWhatsAppContent()) {
                return false;
            }

            await props[currentTab].save?.();
            const hasEnoughCredits = checkIfHasEnoughCredits();
            if(!hasEnoughCredits) {
                setGlobalState('ui.show.isOpenUpgradeModal', true);
            } else {
                if(onHasEnoughCredits) {
                    onHasEnoughCredits();
                } else {
                    const result = await props[currentTab].send();
                    if(result) {
                        setSendingTab(currentTab);
                        setCurrentTab('success');
                    }
                }
            }
        } catch (error) {
            console.error(`Error sending ${currentTab} message:`, error);
            return false;
        }
    };

    const handleActualSend = async () => {
        try {
            await props[currentTab].save?.();
            if(!validateWhatsAppContent()) {
                return false;
            }
            let result = false;
            if(isTrialSendFlow) {
                if(!trialContacts || trialContacts.length === 0) {
                    return false;
                }
                result = await props[currentTab].sendTrial(trialContacts);
            }
            else {
                result = await props[currentTab].send();
            } if(result) {
                setSendingTab(currentTab);
                setGlobalState('ui.show.isOpenUpgradeModal', false);
                setTrialContacts(null);
                setIsTrialSendFlow(false);
                setCurrentTab('success');
            }
            return result;
        } catch (error) {
            console.error(`Error sending ${currentTab} message:`, error);
            return false;
        }
    };

    const handleTrialSend = useCallback(async (contacts: string[]) => {
        try {
            if(!validateWhatsAppContent()) {
                return false;
            }

            await props[currentTab].save?.();
            const hasEnoughCredits = checkIfHasEnoughCredits();
            if(!hasEnoughCredits) {
                setTrialContacts(contacts);
                setIsTrialSendFlow(true);
                setGlobalState('ui.show.isOpenUpgradeModal', true);
            } else {
                const result = await props[currentTab].sendTrial(contacts);   
                if(result) {
                    setSendingTab(currentTab);
                }
                return result;
            }
        } catch (err) {
            console.error(`Error sending trial ${currentTab}:`, err);
            return false;
        }
    }, [currentTab, props]);

    useEffect(() => {
        if(!getEventSettingsXHR) {
            getEventSettings().then();
            return;
        }
        if(getEventSettingsXHR !== 'SUCCESS' || currentTab !== null) {
            return;
        }
        const resolveInitialTab = (): TabType => {
            if(initialTab && filteredAllowedList.includes(initialTab)) {
                return initialTab;
            }
            return whatsappAllowed ? 'whatsapp' : 'email';
        };
        setCurrentTab(resolveInitialTab());
    }, [getEventSettingsXHR, currentTab, initialTab, filteredAllowedList, whatsappAllowed]);

    return {
        currentTab,
        sendingTab,
        handleTabChange,
        handleSave,
        handleSend,
        handleActualSend,
        handleTrialSend,
        filteredAllowedList,
        sendingXHR,
        saveXHR
    };
};
